import { HTMLAttributes } from 'react'
import { map } from 'lodash'
import Link from 'next/link'

import Button from '@/components/base/button-more'
import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Image from '@/components/base/lazy-image'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'
import Heading from '@/components/sections/heading'
import Spacer from '@/components/sections/spacer'
import { useAppStore } from '@/contexts/store'

import { useGridMotion } from './motion/grid'
import { cn } from '@/lib/utils'
import { WPImage } from '@/types'

export type ProductsGridProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    collection: {
      link: string
      title: string
      content: string
      notes: string
      image: WPImage
    }[]
  }>

const ProductsGrid = ({ collection, ...props }: ProductsGridProps) => {
  const { strings } = useAppStore()
  const { motionRef } = useGridMotion()

  return (
    <>
      <Spacer variant="2xl" />

      <Heading />

      <Spacer variant="sm" />

      <Section ref={motionRef} {...props}>
        <Container>
          <Grid className="items-start gap-y-[3.2rem] md:gap-y-[5.6rem]">
            {map(collection, ({ link, title, content, notes, image }, index) => (
              <Grid
                key={`product-item-${index}`}
                className={cn(
                  'col-span-2 md:grid-cols-3 lg:grid-cols-6',
                  'col-span-2 md:col-span-3 lg:col-span-6',
                  'size-full grid-rows-[auto_1fr] lg:grid-rows-none',
                  'gap-y-[3.6rem] lg:gap-y-[4.2rem]'
                )}
                data-motion-card
              >
                <div className={cn('col-span-2 md:col-span-3', 'grid')}>
                  <Link
                    href={link}
                    scroll={false}
                    aria-label={title}
                    className={cn(
                      'rounded-[0.3rem] bg-light',
                      'p-[6.5rem] lg:p-[3.5rem] xl:p-[5.5rem]',
                      'focus-ring-dark'
                    )}
                  >
                    <span className="relative top-1/2 flex aspect-[243/300] w-full -translate-y-1/2 mix-blend-multiply lg:aspect-[287/480]">
                      <Image
                        className="object-contain mix-blend-multiply"
                        src={image.sizes?.medium?.url || image.url}
                        alt=""
                        fill
                      />
                    </span>
                  </Link>
                </div>

                <div
                  className={cn(
                    'col-span-2 md:col-span-3',
                    'grid items-start gap-y-[3.6rem] lg:gap-y-[4.2rem]'
                  )}
                >
                  <div className="grid gap-y-[3.6rem] lg:gap-y-[4.2rem]">
                    <Typography
                      variant="standard"
                      paragraph
                      className="font-bold capitalize !leading-[120%]"
                    >
                      {title}
                    </Typography>

                    <Typography variant="breadcrumbs" paragraph className="!leading-[150%]">
                      {content}
                    </Typography>

                    <Typography variant="breadcrumbs" paragraph className="!leading-[150%]">
                      {notes}
                    </Typography>
                  </div>

                  <div className="mt-auto flex">
                    <Button href={link}>{strings?.labels?.find}</Button>
                  </div>
                </div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Section>
    </>
  )
}

export default ProductsGrid
